import { supabase, TABLES } from '../utils/supabase';
import { User, Asset, Property, Transaction, AIInsight, DocumentUpload, BudgetCategory, Budget, Goal, CalendarEvent, Collaborator, CustomRule } from '../types';

// User Services
export const userService = {
  async getCurrentUser(): Promise<User | null> {
    if (!supabase) return null;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from(TABLES.USERS)
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return data;
  },

  async createUser(userData: Partial<User>): Promise<User | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.USERS)
      .insert([userData])
      .select()
      .single();

    if (error) {
      console.error('Error creating user:', error);
      return null;
    }

    return data;
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<User | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.USERS)
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating user:', error);
      return null;
    }

    return data;
  }
};

// Property Services
export const propertyService = {
  async getProperties(): Promise<Property[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.PROPERTIES)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching properties:', error);
      return [];
    }

    return data || [];
  },

  async createProperty(propertyData: Omit<Property, 'id' | 'created_at' | 'updated_at'>): Promise<Property | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.PROPERTIES)
      .insert([propertyData])
      .select()
      .single();

    if (error) {
      console.error('Error creating property:', error);
      return null;
    }

    return data;
  },

  async updateProperty(propertyId: string, updates: Partial<Property>): Promise<Property | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.PROPERTIES)
      .update(updates)
      .eq('id', propertyId)
      .select()
      .single();

    if (error) {
      console.error('Error updating property:', error);
      return null;
    }

    return data;
  },

  async deleteProperty(propertyId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from(TABLES.PROPERTIES)
      .delete()
      .eq('id', propertyId);

    if (error) {
      console.error('Error deleting property:', error);
      return false;
    }

    return true;
  }
};

// Transaction Services
export const transactionService = {
  async getTransactions(): Promise<Transaction[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .select('*')
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }

    return data || [];
  },

  async createTransaction(transactionData: Omit<Transaction, 'id' | 'created_at'>): Promise<Transaction | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .insert([transactionData])
      .select()
      .single();

    if (error) {
      console.error('Error creating transaction:', error);
      return null;
    }

    return data;
  },

  async updateTransaction(transactionId: string, updates: Partial<Transaction>): Promise<Transaction | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .update(updates)
      .eq('id', transactionId)
      .select()
      .single();

    if (error) {
      console.error('Error updating transaction:', error);
      return null;
    }

    return data;
  },

  async deleteTransaction(transactionId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .delete()
      .eq('id', transactionId);

    if (error) {
      console.error('Error deleting transaction:', error);
      return false;
    }

    return true;
  },

  async bulkCreateTransactions(transactions: Omit<Transaction, 'id' | 'created_at'>[]): Promise<Transaction[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .insert(transactions)
      .select();

    if (error) {
      console.error('Error bulk creating transactions:', error);
      return [];
    }

    return data || [];
  }
};

// Asset Services
export const assetService = {
  async getAssets(): Promise<Asset[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.ASSETS)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching assets:', error);
      return [];
    }

    return data || [];
  },

  async createAsset(assetData: Omit<Asset, 'id' | 'created_at' | 'updated_at'>): Promise<Asset | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.ASSETS)
      .insert([assetData])
      .select()
      .single();

    if (error) {
      console.error('Error creating asset:', error);
      return null;
    }

    return data;
  },

  async updateAsset(assetId: string, updates: Partial<Asset>): Promise<Asset | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.ASSETS)
      .update(updates)
      .eq('id', assetId)
      .select()
      .single();

    if (error) {
      console.error('Error updating asset:', error);
      return null;
    }

    return data;
  },

  async deleteAsset(assetId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from(TABLES.ASSETS)
      .delete()
      .eq('id', assetId);

    if (error) {
      console.error('Error deleting asset:', error);
      return false;
    }

    return true;
  }
};

// AI Insight Services
export const aiInsightService = {
  async getInsights(): Promise<AIInsight[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.AI_INSIGHTS)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching insights:', error);
      return [];
    }

    return data || [];
  },

  async createInsight(insightData: Omit<AIInsight, 'id' | 'created_at'>): Promise<AIInsight | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from(TABLES.AI_INSIGHTS)
      .insert([insightData])
      .select()
      .single();

    if (error) {
      console.error('Error creating insight:', error);
      return null;
    }

    return data;
  }
};

// Document Upload Services
export const documentUploadService = {
  async getDocumentUploads(): Promise<DocumentUpload[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('document_uploads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching document uploads:', error);
      return [];
    }

    return data || [];
  },

  async createDocumentUpload(uploadData: Omit<DocumentUpload, 'id' | 'created_at'>): Promise<DocumentUpload | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('document_uploads')
      .insert([uploadData])
      .select()
      .single();

    if (error) {
      console.error('Error creating document upload:', error);
      return null;
    }

    return data;
  },

  async updateDocumentUpload(uploadId: string, updates: Partial<DocumentUpload>): Promise<DocumentUpload | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('document_uploads')
      .update(updates)
      .eq('id', uploadId)
      .select()
      .single();

    if (error) {
      console.error('Error updating document upload:', error);
      return null;
    }

    return data;
  },

  async deleteDocumentUpload(uploadId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('document_uploads')
      .delete()
      .eq('id', uploadId);

    if (error) {
      console.error('Error deleting document upload:', error);
      return false;
    }

    return true;
  }
};

// Enhanced Transaction Services
export const enhancedTransactionService = {
  ...transactionService,
  
  async getTransactionsByProperty(propertyId: string): Promise<Transaction[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .select('*')
      .eq('property_id', propertyId)
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching property transactions:', error);
      return [];
    }

    return data || [];
  },

  async getTransactionsByClassification(classification: 'personal' | 'business'): Promise<Transaction[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from(TABLES.TRANSACTIONS)
      .select('*')
      .eq('classification', classification)
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching transactions by classification:', error);
      return [];
    }

    return data || [];
  }
};

// Budget Category Services
export const budgetCategoryService = {
  async getBudgetCategories(): Promise<BudgetCategory[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('budget_categories')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching budget categories:', error);
      return [];
    }

    return data || [];
  },

  async createBudgetCategory(categoryData: Omit<BudgetCategory, 'id' | 'created_at' | 'updated_at'>): Promise<BudgetCategory | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('budget_categories')
      .insert([categoryData])
      .select()
      .single();

    if (error) {
      console.error('Error creating budget category:', error);
      return null;
    }

    return data;
  },

  async updateBudgetCategory(categoryId: string, updates: Partial<BudgetCategory>): Promise<BudgetCategory | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('budget_categories')
      .update(updates)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating budget category:', error);
      return null;
    }

    return data;
  },

  async deleteBudgetCategory(categoryId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('budget_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      console.error('Error deleting budget category:', error);
      return false;
    }

    return true;
  }
};

// Budget Services
export const budgetService = {
  async getBudgets(): Promise<Budget[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('budgets')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching budgets:', error);
      return [];
    }

    return data || [];
  },

  async createBudget(budgetData: Omit<Budget, 'id' | 'created_at' | 'updated_at'>): Promise<Budget | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('budgets')
      .insert([budgetData])
      .select()
      .single();

    if (error) {
      console.error('Error creating budget:', error);
      return null;
    }

    return data;
  },

  async updateBudget(budgetId: string, updates: Partial<Budget>): Promise<Budget | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('budgets')
      .update(updates)
      .eq('id', budgetId)
      .select()
      .single();

    if (error) {
      console.error('Error updating budget:', error);
      return null;
    }

    return data;
  },

  async deleteBudget(budgetId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('budgets')
      .delete()
      .eq('id', budgetId);

    if (error) {
      console.error('Error deleting budget:', error);
      return false;
    }

    return true;
  }
};

// Goal Services
export const goalService = {
  async getGoals(): Promise<Goal[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching goals:', error);
      return [];
    }

    return data || [];
  },

  async createGoal(goalData: Omit<Goal, 'id' | 'created_at' | 'updated_at'>): Promise<Goal | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('goals')
      .insert([goalData])
      .select()
      .single();

    if (error) {
      console.error('Error creating goal:', error);
      return null;
    }

    return data;
  },

  async updateGoal(goalId: string, updates: Partial<Goal>): Promise<Goal | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('goals')
      .update(updates)
      .eq('id', goalId)
      .select()
      .single();

    if (error) {
      console.error('Error updating goal:', error);
      return null;
    }

    return data;
  },

  async deleteGoal(goalId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', goalId);

    if (error) {
      console.error('Error deleting goal:', error);
      return false;
    }

    return true;
  }
};

// Calendar Event Services
export const calendarEventService = {
  async getCalendarEvents(): Promise<CalendarEvent[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .order('event_date', { ascending: true });

    if (error) {
      console.error('Error fetching calendar events:', error);
      return [];
    }

    return data || [];
  },

  async createCalendarEvent(eventData: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>): Promise<CalendarEvent | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('calendar_events')
      .insert([eventData])
      .select()
      .single();

    if (error) {
      console.error('Error creating calendar event:', error);
      return null;
    }

    return data;
  },

  async updateCalendarEvent(eventId: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('calendar_events')
      .update(updates)
      .eq('id', eventId)
      .select()
      .single();

    if (error) {
      console.error('Error updating calendar event:', error);
      return null;
    }

    return data;
  },

  async deleteCalendarEvent(eventId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', eventId);

    if (error) {
      console.error('Error deleting calendar event:', error);
      return false;
    }

    return true;
  }
};

// Collaborator Services
export const collaboratorService = {
  async getCollaborators(): Promise<Collaborator[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('collaborators')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching collaborators:', error);
      return [];
    }

    return data || [];
  },

  async createCollaborator(collaboratorData: Omit<Collaborator, 'id' | 'created_at' | 'updated_at'>): Promise<Collaborator | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('collaborators')
      .insert([collaboratorData])
      .select()
      .single();

    if (error) {
      console.error('Error creating collaborator:', error);
      return null;
    }

    return data;
  },

  async updateCollaborator(collaboratorId: string, updates: Partial<Collaborator>): Promise<Collaborator | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('collaborators')
      .update(updates)
      .eq('id', collaboratorId)
      .select()
      .single();

    if (error) {
      console.error('Error updating collaborator:', error);
      return null;
    }

    return data;
  },

  async deleteCollaborator(collaboratorId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('collaborators')
      .delete()
      .eq('id', collaboratorId);

    if (error) {
      console.error('Error deleting collaborator:', error);
      return false;
    }

    return true;
  }
};

// Custom Rule Services
export const customRuleService = {
  async getCustomRules(): Promise<CustomRule[]> {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('custom_rules')
      .select('*')
      .order('priority', { ascending: false });

    if (error) {
      console.error('Error fetching custom rules:', error);
      return [];
    }

    return data || [];
  },

  async createCustomRule(ruleData: Omit<CustomRule, 'id' | 'created_at' | 'updated_at'>): Promise<CustomRule | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('custom_rules')
      .insert([ruleData])
      .select()
      .single();

    if (error) {
      console.error('Error creating custom rule:', error);
      return null;
    }

    return data;
  },

  async updateCustomRule(ruleId: string, updates: Partial<CustomRule>): Promise<CustomRule | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from('custom_rules')
      .update(updates)
      .eq('id', ruleId)
      .select()
      .single();

    if (error) {
      console.error('Error updating custom rule:', error);
      return null;
    }

    return data;
  },

  async deleteCustomRule(ruleId: string): Promise<boolean> {
    if (!supabase) return false;

    const { error } = await supabase
      .from('custom_rules')
      .delete()
      .eq('id', ruleId);

    if (error) {
      console.error('Error deleting custom rule:', error);
      return false;
    }

    return true;
  }
}; 